import { useEffect, useState } from "react";
import api, { list, create, update, remove, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["owner", "manager", "crew", "client"];

export default function Settings() {
  const { user: me, can } = useAuth();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [uForm, setUForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [qMsg, setQMsg] = useState("Halo, ini pesan dari Primrose Organizer 🌸");
  const [uSearch, setUSearch] = useState("");
  const [uRole, setURole] = useState("all");
  const [uStatus, setUStatus] = useState("all");
  const [uSort, setUSort] = useState("name-asc");

  const loadUsers = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => {
    list("settings").then((d) => setSettings(d[0] || {})).catch(() => setSettings({}));
    loadUsers();
    if (can(3)) list("clients").then((d) => setClients(d || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingUsers = users.filter((u) => u.role === "client" && !u.active && u.approval_status !== "rejected");
  const displayUsers = (() => {
    const q = uSearch.trim().toLowerCase();
    let arr = users.filter((u) => {
      if (uRole !== "all" && u.role !== uRole) return false;
      if (uStatus === "active" && !u.active) return false;
      if (uStatus === "inactive" && u.active) return false;
      if (q && !((u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))) return false;
      return true;
    });
    const [key, dir] = uSort.split("-");
    arr = [...arr].sort((a, b) => {
      let va, vb;
      if (key === "role") { va = a.role || ""; vb = b.role || ""; }
      else if (key === "status") { va = a.active ? 1 : 0; vb = b.active ? 1 : 0; }
      else { va = (a[key] || "").toString().toLowerCase(); vb = (b[key] || "").toString().toLowerCase(); }
      if (va < vb) return dir === "asc" ? -1 : 1;
      if (va > vb) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  })();
  const clientName = (id) => { const c = clients.find((x) => x.id === id); return c ? `${c.groom} & ${c.bride}` : null; };

  const saveSettings = async () => {
    setSaving(true);
    try {
      if (settings.id) await update("settings", settings.id, settings);
      else { const c = await create("settings", settings); setSettings(c); }
      toast.success("Pengaturan disimpan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveUser = async () => {
    try {
      if (uForm.id) await api.put(`/users/${uForm.id}`, uForm);
      else await api.post("/users", uForm);
      toast.success("User disimpan"); setUForm(null); loadUsers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delUser = async (id) => { await api.delete(`/users/${id}`); toast.success("User dihapus"); loadUsers(); };
  const setApproval = async (id, active) => {
    try {
      const r = await api.post(`/users/${id}/${active ? "approve" : "reject"}`);
      const emailNote = r.data?.email_sent ? " Email pemberitahuan terkirim." : " (email tidak terkirim)";
      toast.success((active ? "Client disetujui." : "Client ditolak.") + emailNote);
      loadUsers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const linkClient = async (id, clientId) => {
    try { await api.post(`/users/${id}/link-client`, { client_id: clientId }); toast.success("Akun client ditautkan ke event"); loadUsers(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const set = (k, v) => setSettings((p) => ({ ...p, [k]: v }));
  const openQuickWa = () => {
    const digits = String(testTo || "").replace(/[^0-9]/g, "").replace(/^0/, "62");
    if (!digits) { toast.error("Isi nomor WhatsApp dulu"); return; }
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(qMsg)}`, "_blank");
  };

  return (
    <div>
      <PageHeader overline="Konfigurasi Sistem" title="Settings" subtitle="Kelola profil perusahaan dan pengguna sistem." />
      <Tabs defaultValue="company">
        <TabsList className="bg-beige">
          <TabsTrigger value="company" data-testid="tab-company">Perusahaan</TabsTrigger>
          {can(3) && <TabsTrigger value="reminder" data-testid="tab-reminder">WhatsApp</TabsTrigger>}
          {can(3) && <TabsTrigger value="users" data-testid="tab-users">Pengguna</TabsTrigger>}
          {can(3) && (
            <TabsTrigger value="pending" data-testid="tab-pending">
              Menunggu Persetujuan
              {pendingUsers.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-600 text-white" data-testid="pending-badge">{pendingUsers.length}</span>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="company">
          <div className="card-lux p-6 max-w-xl space-y-4 mt-4">
            {[["company_name", "Nama Perusahaan"], ["address", "Alamat"], ["phone", "Telepon"], ["email", "Email"], ["invoice_footer", "Footer Invoice"]].map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs font-semibold text-[#4A4A4A] mb-1.5 block">{l}</Label>
                <Input value={settings?.[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`settings-${k}`} className="bg-white" disabled={!can(3)} />
              </div>
            ))}
            {can(3) && <Button onClick={saveSettings} disabled={saving} data-testid="settings-save" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full">Simpan</Button>}
          </div>
        </TabsContent>

        {can(3) && (
          <TabsContent value="reminder">
            <div className="mt-4 max-w-xl space-y-4">
              <div className="card-lux p-5 border-l-4 border-l-emerald-500">
                <h3 className="font-heading text-lg text-burgundy mb-1">WhatsApp Manual</h3>
                <p className="text-sm text-[#4A4A4A]">Pengiriman WhatsApp bersifat <b>manual</b>. Klik tombol <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">WA</span> di halaman <b>Payments</b> (pengingat pembayaran ke client) atau di tab <b>Vendor / Payment</b> pada detail Event — WhatsApp akan terbuka otomatis dengan pesan yang sudah siap kirim.</p>
                <p className="text-xs text-[#7A7A7A] mt-2">Pastikan nomor WhatsApp client & vendor sudah terisi agar tombol berfungsi.</p>
              </div>

              <div className="card-lux p-5 space-y-3">
                <h3 className="font-heading text-lg text-burgundy">Kirim Pesan Cepat</h3>
                <div>
                  <Label className="text-xs font-semibold text-[#4A4A4A] mb-1.5 block">Nomor WhatsApp</Label>
                  <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="08123456789" data-testid="wa-quick-to" className="bg-white" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#4A4A4A] mb-1.5 block">Pesan</Label>
                  <textarea value={qMsg} onChange={(e) => setQMsg(e.target.value)} rows={3} data-testid="wa-quick-msg" className="w-full border border-gold/30 rounded-lg px-3 py-2 text-sm bg-white" />
                </div>
                <Button onClick={openQuickWa} data-testid="wa-quick-send" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">Buka WhatsApp</Button>
              </div>
            </div>
          </TabsContent>
        )}

        {can(3) && (
          <TabsContent value="users">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3 mt-4">
              <Input value={uSearch} onChange={(e) => setUSearch(e.target.value)} placeholder="Cari nama / email…" data-testid="user-search" className="bg-white sm:max-w-xs" />
              <Select value={uRole} onValueChange={setURole}>
                <SelectTrigger className="w-full sm:w-36 bg-white" data-testid="user-filter-role"><SelectValue placeholder="Role" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Semua Role</SelectItem>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={uStatus} onValueChange={setUStatus}>
                <SelectTrigger className="w-full sm:w-36 bg-white" data-testid="user-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Semua Status</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
              <Select value={uSort} onValueChange={setUSort}>
                <SelectTrigger className="w-full sm:w-44 bg-white" data-testid="user-sort"><SelectValue placeholder="Urutkan" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="name-asc">Nama A–Z</SelectItem><SelectItem value="name-desc">Nama Z–A</SelectItem>
                  <SelectItem value="email-asc">Email A–Z</SelectItem><SelectItem value="role-asc">Role A–Z</SelectItem>
                  <SelectItem value="status-desc">Status: Active dulu</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => setUForm({ name: "", email: "", password: "", role: "crew", phone: "" })} data-testid="user-add-btn" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full sm:ml-auto shrink-0"><Plus className="w-4 h-4 mr-1" />User</Button>
            </div>
            <div className="card-lux overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-beige/60 hover:bg-beige/60 border-gold/20">
                    {["Nama", "Email", "Role", "Status", "Aksi"].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">{h}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayUsers.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-[#7A7A7A] py-6" data-testid="user-empty">Tidak ada pengguna sesuai filter.</TableCell></TableRow>}
                  {displayUsers.map((u) => (
                    <TableRow key={u.id} className="border-gold/10 hover:bg-beige/30" data-testid="user-row">
                      <TableCell className="text-sm font-medium text-[#1A1A1A]">{u.name}</TableCell>
                      <TableCell className="text-sm text-[#4A4A4A]">{u.email}</TableCell>
                      <TableCell className="text-sm capitalize">{u.role}</TableCell>
                      <TableCell><StatusBadge status={u.active ? "Active" : "Cancelled"} /></TableCell>
                      <TableCell className="whitespace-nowrap">
                        {u.role === "client" && (u.active
                          ? <button onClick={() => setApproval(u.id, false)} className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 mr-1" data-testid="user-reject">Tolak</button>
                          : <button onClick={() => setApproval(u.id, true)} className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 mr-1" data-testid="user-approve">Setujui</button>)}
                        <button onClick={() => setUForm({ ...u, password: "" })} className="p-1.5 text-[#7A7A7A] hover:text-burgundy" data-testid="user-edit"><Pencil className="w-4 h-4" /></button>
                        {can(4) && u.id !== me?.id && <button onClick={() => { if (window.confirm(`Hapus user ${u.name}?`)) delUser(u.id); }} className="p-1.5 text-[#7A7A7A] hover:text-red-600" data-testid="user-delete"><Trash2 className="w-4 h-4" /></button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}

        {can(3) && (
          <TabsContent value="pending">
            <div className="card-lux overflow-hidden mt-4">
              {pendingUsers.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#7A7A7A]" data-testid="pending-empty">Tidak ada pendaftar yang menunggu persetujuan. 🌸</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-beige/60 hover:bg-beige/60 border-gold/20">
                      {["Nama", "Email", "Tautan Event", "Aksi"].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">{h}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingUsers.map((u) => (
                      <TableRow key={u.id} className="border-gold/10 hover:bg-beige/30" data-testid="pending-row">
                        <TableCell className="text-sm font-medium text-[#1A1A1A]">{u.name}</TableCell>
                        <TableCell className="text-sm text-[#4A4A4A]">{u.email}</TableCell>
                        <TableCell className="text-sm">
                          <Select value={u.client_id || ""} onValueChange={(v) => linkClient(u.id, v)}>
                            <SelectTrigger className="h-8 w-52" data-testid="pending-link-client"><SelectValue placeholder={u.client_id ? clientName(u.client_id) || "Pilih client…" : "Pilih client…"} /></SelectTrigger>
                            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.groom} & {c.bride}</SelectItem>)}</SelectContent>
                          </Select>
                          {!u.client_id && <p className="text-[10px] text-red-600 mt-1">Belum tertaut ke event</p>}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <button onClick={() => setApproval(u.id, true)} className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 mr-1 font-semibold" data-testid="pending-approve">Setujui</button>
                          <button onClick={() => setApproval(u.id, false)} className="text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-700 font-semibold" data-testid="pending-reject">Tolak</button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!uForm} onOpenChange={(o) => !o && setUForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-burgundy">{uForm?.id ? "Edit User" : "Tambah User"}</DialogTitle></DialogHeader>
          {uForm && (
            <div className="space-y-3 py-2">
              <div><Label className="text-xs font-semibold mb-1 block">Nama</Label><Input value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} data-testid="user-name" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Email</Label><Input value={uForm.email} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} data-testid="user-email" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Password {uForm.id && "(kosongkan jika tidak diubah)"}</Label><Input type="password" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} data-testid="user-password" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">No. WhatsApp</Label><Input value={uForm.phone || ""} onChange={(e) => setUForm({ ...uForm, phone: e.target.value })} data-testid="user-phone" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Role</Label>
                <Select value={uForm.role} onValueChange={(v) => setUForm({ ...uForm, role: v })}><SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent></Select>
              </div>
              {uForm.id && <div><Label className="text-xs font-semibold mb-1 block">Aktif</Label>
                <Select value={String(uForm.active)} onValueChange={(v) => setUForm({ ...uForm, active: v === "true" })}><SelectTrigger data-testid="user-active"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="true">Aktif</SelectItem><SelectItem value="false">Nonaktif</SelectItem></SelectContent></Select>
              </div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUForm(null)}>Batal</Button>
            <Button onClick={saveUser} data-testid="user-save" className="bg-burgundy hover:bg-burgundy-dark text-white">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
